// src/store/Slice/repairSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = import.meta.env.VITE_API_URL;
const API_URL = `${BASE_URL}/api/repair`;

const token = () => localStorage.getItem("jwt");

export interface Repair {
  _id: string;
  orderNumber: string;
  requestType: "repair" | "replacement";
  companyName: string;
  createdBy?: { username: string; employeeId: string };
  receivingDate?: string;
  expectedDeliveryDate?: string;
  issueDescription?: string;
  remark?: string;
  serialId?: string;
  isApproved?: boolean;
  status?: string;
}

interface RepairState {
  items: Repair[];
  total: number;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  message: string | null;
  lastSeq: number;
  lastOrder: string | null;
}

const initialState: RepairState = {
  items: [],
  total: 0,
  status: "idle",
  error: null,
  message: null,
  lastSeq: 0,
  lastOrder: null,
};

// Async Thunks
export const fetchRepairsAsync = createAsyncThunk(
  "repairs/fetchRepairs",
  async (params: any) => {
    const res = await axios.get(API_URL, {
      headers: { Authorization: `Bearer ${token()}` },
      params,
    });
    return res.data;
  }
);

export const deleteRepairAsync = createAsyncThunk(
  "repairs/deleteRepair",
  async (id: string) => {
    const res = await axios.delete(`${API_URL}/${id}`, {
      headers: { Authorization: `Bearer ${token()}` },
    });
    return res.data;
  }
);

export const createRepairAsync = createAsyncThunk(
  "repairs/createRepair",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await axios.post(API_URL, payload, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// fetch last repair seq (month-wise, global)
export const fetchLastRepairSeqAsync = createAsyncThunk(
  "repair/lastSeq",
  async ({ requestType }: { requestType: "repair" | "replacement" }) => {
    const res = await axios.get(`${API_URL}/last-seq`, {
      params: { requestType },
      headers: { Authorization: `Bearer ${token()}` },
    });
    return res.data;
  }
);




export const approveRepairAsync = createAsyncThunk(
  "repairs/approveRepair",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`${API_URL}/approve/${id}`, {}, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const updateRepairAsync = createAsyncThunk(
  "repairs/updateRepair",
  async ({ id, data }: { id: string; data: Partial<Repair> }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${API_URL}/${id}`, data, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Slice
const repairSlice = createSlice({
  name: "repairs",
  initialState,
  reducers: {
    clearMessages(state) {
      state.message = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // fetchRepairsAsync
      .addCase(fetchRepairsAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchRepairsAsync.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload.data || [];
        state.total = action.payload.total ?? state.items.length;
      })
      .addCase(fetchRepairsAsync.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch repairs";
      })


      // fetchLastRepairSeqAsync
      .addCase(fetchLastRepairSeqAsync.fulfilled, (state, action) => {
        const payload = action.payload ?? {};
        state.lastSeq = payload.lastSeq; // backend already +1
        state.lastOrder = payload.lastOrder ?? null;
      }) 
      .addCase(fetchLastRepairSeqAsync.rejected, (state, action) => {
        state.lastSeq = 1;
        state.lastOrder = null;
        state.error = action.error.message || "Failed to fetch last sequence";
      })


    // createRepairAsync
    .addCase(createRepairAsync.pending, (state) => {
      state.status = "loading";
    })
    .addCase(createRepairAsync.fulfilled, (state, action) => {
      state.status = "succeeded";
      if (Array.isArray(action.payload)) {
        state.items.push(...action.payload);
      } else {
        state.items.push(action.payload);
      }
      state.message = "Repair(s) created successfully";
    })
    .addCase(createRepairAsync.rejected, (state, action) => {
      state.status = "failed";
      state.error = action.payload as string || "Failed to create repair(s)";
    })

    // approveRepairAsync
    .addCase(approveRepairAsync.fulfilled, (state, action) => {
      state.message = "Repair approved successfully";
      const updated = action.payload?.data || action.payload;
      if (updated?._id) {
        const index = state.items.findIndex((r) => r._id === updated._id);
        if (index !== -1) state.items[index] = updated;
      }
    })
    .addCase(approveRepairAsync.rejected, (state, action) => {
      state.error = action.payload as string || "Failed to approve repair";
    })

    // updateRepairAsync
    .addCase(updateRepairAsync.fulfilled, (state, action) => {
      state.message = "Repair updated successfully";
      const updated = action.payload?.data || action.payload;
      const index = state.items.findIndex(r => r._id === updated._id);
      if (index !== -1) state.items[index] = updated;
    })
    .addCase(updateRepairAsync.rejected, (state, action) => {
      state.error = action.payload as string || "Failed to update repair";
    })

    // deleteRepairAsync
    .addCase(deleteRepairAsync.fulfilled, (state) => {
      state.message = "Repair deleted successfully";
    })
    .addCase(deleteRepairAsync.rejected, (state, action) => {
      state.error = action.error.message || "Failed to delete repair";
    });
},
});

export const { clearMessages } = repairSlice.actions;
export default repairSlice.reducer;
